
import { assertNever, filterNilValues, getCollectionIds } from './typeUtils';
import { cloneDeep, concat, filter, find, first, forEach, groupBy, intersection, isEmpty, map, reduce, size, sortBy } from 'es-toolkit/compat';
import { core } from '@tock/coretypes';
import { validateSelectedOptionsForGroup } from './supplementsUtils';

interface SlimOption {
  id?: number | null;
  backingMode?: core.BackingMode | null;
  ticketOptionGroupId?: number | null;
}

export function findOrdersMatchingOptions(
  orders: core.IOptionOrder[] | null | undefined,
  options: SlimOption[] | undefined
) {
  if (!orders || !options) {
    return [];
  }
  const filteredOrders: core.IOptionOrder[] = [];
  forEach(orders, (order) => {
    if (find(options, { id: order.optionId, backingMode: order.backingMode })) {
      filteredOrders.push(order);
    }
    forEach(order.modifierOption as core.OptionOrder[], (modifier) => {
      if (find(options, { id: modifier.optionId, backingMode: modifier.backingMode })) {
        filteredOrders.push(modifier);
      }
    });
  });
  return filteredOrders;
}

export interface GroupedOptionOrder {
  ticketOptionId: number;
  parentGroupId?: number;
  optionOrders: core.IOptionOrder[];
  numPurchased: number;
  backingMode: core.BackingMode;
}

export function groupOptionOrders(orders: core.IOptionOrder[]) {
  const map: Dictionary<GroupedOptionOrder> = {};
  orders.forEach((optionOrder) => {
    // This key is derived from the entire option order so as to not group option orders if fields like `sentToKitchen` differ
    const key = JSON.stringify(optionOrder);
    if (key && !map[key]) {
      map[key] = {
        ticketOptionId: optionOrder.optionId ?? 0,
        parentGroupId: optionOrder.parentGroupId ?? undefined,
        optionOrders: [optionOrder],
        numPurchased: optionOrder.numPurchased ?? 0,
        backingMode: optionOrder.backingMode ?? 'TICKET_OPTION',
      };
    } else if (key) {
      map[key].optionOrders.push(optionOrder);
      map[key].numPurchased += optionOrder.numPurchased ?? 0;
    }
  });
  return Object.values(map);
}

export function groupOptionOrdersByParentGroupId(orders: core.IOptionOrder[]) {
  return groupBy(groupOptionOrders(orders), 'parentGroupId');
}

export function isRequiredOptionGroup(ticketOptionGroup: core.ITicketOptionGroup) {
  return ticketOptionGroup.optionGroupType === 'FIXED_LIMIT'
    ? ticketOptionGroup.fixedLimitMin ?? 0 > 0
    : ticketOptionGroup.isRequired;
}

export function segmentOptionGroups(
  ticketOptionGroup: core.ITicketOptionGroup[],
  ticketType: core.ITicketType,
  context?: { isFeatureEnabled(featureName: core.Feature.FeatureName): boolean }
) {
  // Filter out modifiers
  const sortedTicketOptionGroups = sortBy(ticketOptionGroup, 'ordering');
  const optionsWithModifiers = filter(
    map(sortedTicketOptionGroups, (group) => group.parentTicketOptionId),
    filterNilValues
  );
  const topLevelOptionGroups = filter(
    sortedTicketOptionGroups,
    (group) => !group.parentTicketOptionId
  );
  const modifierOptionGroups = filter(
    sortedTicketOptionGroups,
    (group) => !!group.parentTicketOptionId
  );
  return {
    optionsWithModifiers,
    topLevelOptionGroups,
    modifierOptionGroups,
  };
}

export function segmentOptionGroupsAndOptions(
  ticketOptionGroup: core.ITicketOptionGroup[],
  ticketOption: core.ITicketOption[],
  ticketType: core.ITicketType,
  context?: { isFeatureEnabled(featureName: core.Feature.FeatureName): boolean }
) {
  const segmentOptionGroupValues = segmentOptionGroups(ticketOptionGroup, ticketType, context);
  const topLevelTicketOptionIds = getCollectionIds(segmentOptionGroupValues.topLevelOptionGroups);

  const topLevelTicketOptions = ticketOption.filter(
    (option) =>
      !!option.ticketOptionGroupId && topLevelTicketOptionIds.includes(option.ticketOptionGroupId)
  );

  return {
    ...segmentOptionGroupValues,
    topLevelTicketOptions,
  };
}

/**
 * Validates whether the options selected in the consumer flow match the requirements of their option group.
 * Should be called for each option group at least once in the checkout flow.
 *
 * @param orders Mapping of optionId to quantity selected
 * @param optionsInGroup List of all the options in the provided option group
 * @param partySize The size of the booking party
 * @param optionGroup The option group for which we should be validating the rules
 * @returns Whether the option group's rules are being met
 */
export function isOptionGroupValid(
  orders: core.IOptionOrder[] | null | undefined,
  optionsInGroup: core.ITicketOption[] | undefined,
  partySize: number,
  optionGroup: core.ITicketOptionGroup,
  rules?: {
    waiveMinimumRequirements?: boolean;
  }
) {
  const optionsWithCount = map(optionsInGroup, (option: core.ITicketOption) => {
    return {
      count: size(findOrdersMatchingOptions(orders, [option])),
      ticketOptionGroupId: optionGroup.id,
    };
  });

  return validateSelectedOptionsForGroup(optionGroup, optionsWithCount, partySize, {
    waiveMinimumRequirements: rules?.waiveMinimumRequirements,
  });
}

/**
 * Counts the total quantity of supplements in the option group. (Options need to have been pre-filtered to one group).
 *
 * @param allSelectedOptions A mapping of optionId to quantity selected
 * @param optionsInOptionGroup A list of the options we should count
 * @returns The total quantity of supplements selected in the group
 */
export function getSelectedOptionsCount(
  allSelectedOptions: core.IOptionOrder[],
  optionsInOptionGroup: core.ITicketOption[]
): number {
  return size(findOrdersMatchingOptions(allSelectedOptions, optionsInOptionGroup));
}

const hasAllOptionExclusion = (
  ticketOptionGroup: core.ITicketOptionGroup,
  ticketOptions: core.ITicketOption[],
  optionExclusion: core.IOptionExclusion[] | undefined | null
) => {
  if (!isRequiredOptionGroup(ticketOptionGroup)) {
    return false;
  }
  // Precedence is assumed for data fixes that were done on the option exclusions table
  if (optionExclusion && !isEmpty(optionExclusion)) {
    // If there are more exclusions than all combinations, then assume everything is excluded from each other.
    // TODO: Improve this logic.
    return optionExclusion.length >= (ticketOptions.length * (ticketOptions.length - 1)) / 2;
  } else if (isRequiredOptionGroup(ticketOptionGroup) && ticketOptionGroup.hasExclusions) {
    return true;
  }
  return false;
};

/**
 * We only support option exclusions for ticket option-backed
 * options (i.e. not Menus stuff at the moment). If we want to
 * support them across menus, we need to migrate option
 * exclusions to have a backing mode and then update this
 * function accordingly. - CM 03/2021
 */
export const getIsExcluded = (
  ticketOptionId: number,
  ticketOptionGroup: core.ITicketOptionGroup,
  ticketOptions: core.ITicketOption[],
  optionOrders: core.IOptionOrder[],
  optionExclusion: core.IOptionExclusion[] | undefined | null
) => {
  // precedence is assumed for values from the option exclusions table so that
  // setting "hasExclusions" checkbox in dashboard won't break existing exclusions
  const selections = optionOrders;
  let excluded = false;
  if (size(optionExclusion) > 0) {
    forEach(optionExclusion as core.OptionExclusion[], (exclusion) => {
      if (
        (exclusion.selectedOptionId === ticketOptionId &&
          findOrdersMatchingOptions(selections, [
            { id: exclusion.excludedOptionId, backingMode: 'TICKET_OPTION' },
          ]).length > 0) ||
        (exclusion.excludedOptionId === ticketOptionId &&
          findOrdersMatchingOptions(selections, [
            { id: exclusion.selectedOptionId, backingMode: 'TICKET_OPTION' },
          ]).length > 0)
      ) {
        excluded = true;
      }
    });
  } else if (ticketOptionGroup.hasExclusions) {
    const ticketOptionIds = map(ticketOptions, (ticketOption) => {
      return ticketOption.id as number;
    });
    const selectedIds = map(findOrdersMatchingOptions(selections, ticketOptions), (option) => {
      return option.optionId as number;
    });
    const selectionsMadeInCurrentGroup = intersection(ticketOptionIds, selectedIds);
    // if the option is not selected, but another option in the group is selected
    if (
      findOrdersMatchingOptions(selections, [
        { id: ticketOptionId, backingMode: ticketOptionGroup.backingMode },
      ]).length === 0 &&
      !isEmpty(selectionsMadeInCurrentGroup)
    ) {
      excluded = true;
    }
  }

  return excluded;
};

/**
 * Determines the maximum value of a Supplement component's counter, given the type of the supplement group it's in.
 * Just a convenient place to put the logic so we don't need to inline a switch statement in a render function.
 *
 * @param optionGroup The option group (aka supplement)
 * @param partySize The size of party currently in checkout
 * @param numSelectedOfSupplement The number of this supplement selected already
 * @param numSelectedInGroup The number of all supplements in this group selected already
 * @param ticketOptionMaxQuantity
 * @returns The maximum value of the counter
 */
export function getMaxQuantityForSupplementInGroup(
  optionGroup: core.ITicketOptionGroup,
  partySize: number,
  numSelectedOfSupplement: number,
  numSelectedInGroup: number,
  ticketOptionMaxQuantity: number | null | undefined,
  numFiredToKitchen: number
): number {
  const effectiveMax =
    ticketOptionMaxQuantity === null || (ticketOptionMaxQuantity === undefined)
      ? Number.POSITIVE_INFINITY
      : ticketOptionMaxQuantity;

  const disregardMax = getMaxQuantityDisregardingMax(
    optionGroup,
    partySize,
    numSelectedOfSupplement,
    numSelectedInGroup
  );

  return Math.min(disregardMax, effectiveMax + numFiredToKitchen);
}

/**
 * Obtains the minimum required increment of options in the group in the checkout flow.
 * For most, this will be 1, as the increment/decrement buttons change the value by 1.
 * However, some configurations, such as one with a fixed minimum or with a requirement
 * that all in the party must choose the same, the value can differ.
 */
export function getMinRequiredIncrement(
  optionGroup: core.ITicketOptionGroup,
  partySize: number,
  ticketOptions: core.ITicketOption[],
  optionExclusion: core.IOptionExclusion[]
): number {
  const optionGroupType = optionGroup.optionGroupType as core.OptionGroupType;
  switch (optionGroupType) {
    case 'PER_PARTY':
      return 1;
    case 'UNLIMITED':
      return 1;
    case 'FIXED_LIMIT':
      return optionGroup.hasExclusions ? optionGroup.fixedLimitMin ?? 1 : 1;
    case 'PER_PERSON':
      return hasAllOptionExclusion(optionGroup, ticketOptions, optionExclusion) ? partySize : 1;
    default:
      assertNever(optionGroupType);
  }
}

/**
 * This function takes a lot of arguments just to get the value that the modal should
 * default to when clicking to add an option/menu item to an order during checkout.
 * It's influenced by the group it is in, the other options in the group, party size,
 * existing orders and exclusions. The general thrust is that it should be the lowest
 * greater than 0 that is still allowed. This also has the effect that, if the decrement
 * button is clicked from this value, the selector will go to 0.
 */
export function getTicketOptionDefaultSelection({
  ticketOption,
  ticketOptionGroup,
  ticketOptionsInGroup,
  partySize,
  optionOrders,
  partyOptionOrders,
  optionExclusion = [],
}: {
  ticketOption: core.ITicketOption;
  ticketOptionGroup: core.ITicketOptionGroup;
  ticketOptionsInGroup: core.ITicketOption[];
  partySize: number;
  optionOrders: core.IOptionOrder[];
  partyOptionOrders?: core.IOptionOrder[];
  optionExclusion?: core.IOptionExclusion[];
}): number {
  const numSelectedInGroup = getSelectedOptionsCount(
    [...optionOrders, ...(partyOptionOrders || [])],
    ticketOptionsInGroup
  );
  const ordersOfTicketOption = findOrdersMatchingOptions(optionOrders, [ticketOption]);
  const numFiredToKitchen: number = ordersOfTicketOption.filter(
    (option) => option.sentToKitchen
  ).length;
  const minRequiredOfSupplement = getMinRequiredIncrement(
    ticketOptionGroup,
    partySize,
    ticketOptionsInGroup,
    optionExclusion ?? []
  );
  const maxCounterOfSupplement = getMaxQuantityForSupplementInGroup(
    ticketOptionGroup,
    partySize,
    ordersOfTicketOption.length,
    numSelectedInGroup,
    ticketOption?.currentQuantity,
    numFiredToKitchen
  );
  return Math.min(minRequiredOfSupplement, maxCounterOfSupplement - ordersOfTicketOption.length);
}

function getMaxQuantityDisregardingMax(
  optionGroup: core.ITicketOptionGroup,
  partySize: number,
  numSelectedOfSupplement: number,
  numSelectedInGroup: number
): number {
  const optionGroupType = optionGroup.optionGroupType as core.OptionGroupType;
  switch (optionGroupType) {
    case 'PER_PARTY':
      return 1;
    case 'UNLIMITED':
      return Infinity;
    case 'FIXED_LIMIT':
      return numSelectedOfSupplement + (optionGroup.fixedLimitMax ?? 1) - numSelectedInGroup;
    case 'PER_PERSON':
      return numSelectedOfSupplement + (partySize - numSelectedInGroup);
    default:
      assertNever(optionGroupType);
  }
}

export function getMinQuantity(optionGroup: core.ITicketOptionGroup, partySize: number): number {
  const optionGroupType = optionGroup.optionGroupType as core.OptionGroupType;
  switch (optionGroupType) {
    case 'PER_PARTY': {
      return optionGroup.isRequired ? 1 : 0;
    }
    case 'UNLIMITED': {
      return optionGroup.isRequired ? 1 : 0;
    }
    case 'FIXED_LIMIT': {
      return optionGroup.fixedLimitMin ?? 0;
    }
    case 'PER_PERSON': {
      return optionGroup.isRequired ? partySize : 0;
    }
    default: {
      assertNever(optionGroupType);
    }
  }
}

type HelpText = string | undefined;

/*
 * Derives optional help text for the conditional logic applied per addon group.
 * This group logic is defined in the experience add-on editor in dashboard under "Selection limit per add-on"
 *
 * An add-on group has one of three different "optionGroupTypes" applying to the entire group:
 *   "one per person", "one per party", and "unlimited"
 *
 * For any of these options, there are two optional toggles (totalling 4 :checked combinations):
 *   - (isRequired) all guests in party must select an addon from this group
 *   - (hasExclusions) all guests in party must select the same addon
 *
 * That totals 12 different permutations of help text. three types, with four potential combinations each.
 *
 * Here, we are taking the party size and option group, and rendering up to four different messages for each, based on the settings.
 */
export function helpTextFromTicketOptionGroup(
  partySize: number,
  {
    optionGroupType,
    isRequired,
    hasExclusions,
    fixedLimitMin,
    fixedLimitMax,
  }: core.ITicketOptionGroup
): HelpText {
  const type = optionGroupType as core.OptionGroupType;
  isRequired = !!isRequired;
  hasExclusions = !!hasExclusions;
  switch (type) {
    case 'PER_PERSON':
      return helpTextPerPerson(partySize, isRequired);
    case 'PER_PARTY':
      return helpTextPerParty(isRequired, hasExclusions);
    case 'FIXED_LIMIT':
      return helpTextFixedLimit(fixedLimitMin, fixedLimitMax);
    case 'UNLIMITED':
      return helpTextUnlimited(isRequired, hasExclusions);
    default:
      assertNever(type);
  }
}

// the logic for "one per party"
function helpTextPerParty(required: boolean, exclusions: boolean): HelpText {
  if (!required && !exclusions) {
    return 'Select one of each';
  }
  return `Select one`;
}

// the logic for "one per person"
function helpTextPerPerson(partySize: number, required: boolean): HelpText {
  if (partySize === 1) {
    return `Select one`;
  }
  if (required) {
    return `Select ${partySize}`;
  }
  return `Select up to ${partySize}`;
}
// the logic for fixed limit
function helpTextFixedLimit(
  fixedLimitMin: number | undefined | null,
  fixedLimitMax: number | undefined | null
): HelpText {
  const min = fixedLimitMin ?? 0;
  const max = fixedLimitMax ?? 1;
  if (min === 0) {
    return `Choose up to ${max}`;
  }

  const selectionText = min === max ? (min === 1 ? 'one' : `${min}`) : `${min}-${max}`;
  return `Choose ${selectionText}`;
}

// the logic for "unlimited"
function helpTextUnlimited(required: boolean, exclusions: boolean): HelpText {
  if (!required) {
    return 'Select items';
  }
  return `Select one`;
}

export interface SelectedOption {
  id: number;
  count: number;
  name: string;
  amount: number;
  modifier: boolean;
}

export function mapOptionOrders(
  purchase: core.PurchaseOrder | undefined
): Dictionary<SelectedOption> {
  if (!purchase) {
    return {};
  }
  return mapOptionOrdersInternal(purchase.optionOrder, purchase.option);
}

export function mapOptionOrdersInternal(options: core.IOptionOrder[], prices: core.IPrice[]) {
  const priceMap: Dictionary<SelectedOption> = {};

  const purchasedOptions = options;
  if (!purchasedOptions || purchasedOptions.length === 0) {
    return priceMap;
  }

  let uniqueKey = 0;
  forEach(purchasedOptions, (option) => {
    const hasModifiers = size(option.modifierOption) > 0;
    if (!hasModifiers) {
      const key = 'key-o-' + (option.optionId ?? 0);
      const price = find(prices, { id: option.optionId, backingMode: option.backingMode });
      if (priceMap[key]) {
        priceMap[key].count = priceMap[key].count + 1;
      } else {
        priceMap[key] = {
          id: price?.id ?? 0,
          name: price?.name ?? '',
          count: 1,
          amount: price?.amount ?? 0,
          modifier: false,
        };
      }
      return;
    }
    const key = 'key-' + ++uniqueKey;
    const price = find(prices, { id: option.optionId, backingMode: option.backingMode });
    priceMap[key] = {
      id: price?.id ?? 0,
      name: price?.name ?? '',
      count: 1,
      amount: price?.amount ?? 0,
      modifier: false,
    };

    forEach(option.modifierOption, (modifier) => {
      const modifierKey = key + '-m-' + (modifier.optionId ?? 0);
      const price = find(
        prices,
        (price) => price.id === modifier.optionId && price.backingMode === modifier.backingMode
      );
      if (priceMap[modifierKey]) {
        priceMap[modifierKey].count = priceMap[modifierKey].count + 1;
      } else {
        priceMap[modifierKey] = {
          id: price?.id ?? 0,
          name: price?.name ?? '',
          count: 1,
          amount: price?.amount ?? 0,
          modifier: true,
        };
      }
    });
  });

  return priceMap;
}

export function mapPurchasedOptions(pastPurchase?: core.PastPurchase): Dictionary<SelectedOption> {
  const priceMap: Dictionary<SelectedOption> = {};
  const purchasedOptions = pastPurchase?.purchasedOption;
  if (!purchasedOptions || purchasedOptions.length === 0) {
    return priceMap;
  }

  let uniqueKey = 0;
  forEach(purchasedOptions, (option) => {
    const hasModifiers = size(option.modifierOption) > 0;
    if (!hasModifiers) {
      const key = 'key-o-' + (option.id ?? 0);
      if (priceMap[key]) {
        priceMap[key].count = priceMap[key].count + 1;
      } else {
        priceMap[key] = {
          id: option.id ?? 0,
          name: option.name ?? '',
          count: 1,
          amount: option.amount ?? 0,
          modifier: false,
        };
      }
      return;
    }
    const key = 'key-' + ++uniqueKey;
    priceMap[key] = {
      id: option.id ?? 0,
      name: option.name ?? '',
      count: 1,
      amount: option.amount ?? 0,
      modifier: false,
    };

    forEach(option.modifierOption, (modifier) => {
      const modifierKey = key + '-m-' + (modifier.id ?? 0);
      if (priceMap[modifierKey]) {
        priceMap[modifierKey].count = priceMap[modifierKey].count + 1;
      } else {
        priceMap[modifierKey] = {
          id: modifier?.id ?? 0,
          name: modifier?.name ?? '',
          count: 1,
          amount: modifier?.amount ?? 0,
          modifier: true,
        };
      }
    });
  });

  return priceMap;
}

export const generateCounterProps = ({
  id,
  disabled,
  minRequiredIncrement,
  maxQuantity,
  onChangeQuantity,
  quantity,
  numFiredToKitchen,
  adminOverride = false,
}: {
  id: number;
  disabled: boolean;
  minRequiredIncrement: number;
  maxQuantity: number;
  onChangeQuantity: (id: number, newQuantity: number) => void;
  quantity: number;
  numFiredToKitchen: number;
  adminOverride?: boolean;
}) => {
  const disableDecrement = disabled || quantity <= (adminOverride ? 0 : numFiredToKitchen);
  const disableIncrement = disabled || quantity >= maxQuantity;
  const handleDecrementQuantity = () => {
    let newQuantity = quantity - 1;
    if (newQuantity < minRequiredIncrement) {
      newQuantity = 0;
    }
    if (newQuantity >= 0 || disableDecrement) {
      onChangeQuantity(id, newQuantity);
    }
  };
  const handleIncrementQuantity = () => {
    let newQuantity = quantity + 1;
    if (newQuantity < minRequiredIncrement) {
      newQuantity = minRequiredIncrement;
    }
    if (newQuantity <= maxQuantity || disableIncrement) {
      onChangeQuantity(id, newQuantity);
    }
  };

  return {
    disableDecrement,
    disableIncrement,
    handleDecrementQuantity,
    handleIncrementQuantity,
  };
};

export const generateOptionGroupProps = ({
  optionOrders,
  partyOptionOrders,
  ticketOptions,
  onChangeQuantity,
  adminOverride,
  patron,
  ticketOptionGroup,
}: {
  optionOrders: core.IOptionOrder[];
  partyOptionOrders?: core.IOptionOrder[];
  ticketOptions: core.ITicketOption[];
  onChangeQuantity(selectedOptions: core.IOptionOrder[]): void;
  patron?: core.IPatron;
  adminOverride?: boolean;
  ticketOptionGroup: core.ITicketOptionGroup;
}) => {
  const handleChangeQuantity = (ticketOptionId: number, newQuantity: number) => {
    // TODO [BRM 2020/08/26] address possibility of id collisions here
    const ticketOption = find(
      ticketOptions,
      (findTicketOption) => findTicketOption.id === ticketOptionId
    );
    const selectionsWithoutOption = [
      ...filter(optionOrders, (option) => option.optionId !== ticketOptionId),
      ...(partyOptionOrders || []),
    ];

    // todo [BRM 07/20/20] this will need to be updated to take into account decrementing or adding for a specific patron, not just the primary patron
    // final group ordering solution will also need to take into account who fired items belong to
    let patronIdToSelectionCount: Dictionary<number> = {};
    const patronIdToPatron: Dictionary<core.IPatron> = {};

    patronIdToSelectionCount = reduce(
      filter(optionOrders, (option) => option.optionId === ticketOptionId),
      (patronIdToCount, optionOrder) => {
        if (patronIdToCount[optionOrder.patron?.id || -1]) {
          patronIdToCount[optionOrder.patron?.id || -1]++;
        } else {
          patronIdToCount[optionOrder.patron?.id || -1] = 1;
        }

        if (optionOrder.patron?.id && !patronIdToPatron[optionOrder.patron?.id]) {
          patronIdToPatron[optionOrder.patron.id] = optionOrder.patron;
        }
        return patronIdToCount;
      },
      patronIdToSelectionCount
    );

    const optionsSentToKitchen = filter(
      optionOrders,
      (option) => option.optionId === ticketOptionId && !!option.sentToKitchen
    );

    if (!adminOverride && newQuantity < optionsSentToKitchen.length) {
      return;
    }
    const numSentToKitchen = Math.min(newQuantity, optionsSentToKitchen.length);

    const patronIdToSelectionCountEntries = Object.entries(patronIdToSelectionCount);
    let patronIdCountToDecrement = patronIdToSelectionCountEntries.pop();
    for (let i = 0; i < newQuantity; ++i) {
      let patronInternal = patron;
      if (patronIdCountToDecrement && patronIdCountToDecrement[1] !== 0) {
        patronInternal = patronIdToPatron[patronIdCountToDecrement[0]];
        patronIdCountToDecrement[1]--;
      } else if (patronIdCountToDecrement) {
        patronIdCountToDecrement = patronIdToSelectionCountEntries.pop();
        if (patronIdCountToDecrement) {
          patronInternal = patronIdToPatron[patronIdCountToDecrement[0]];
          patronIdCountToDecrement[1]--;
        }
      }
      selectionsWithoutOption.push({
        optionId: ticketOptionId,
        numPurchased: 1,
        sentToKitchen: i < numSentToKitchen,
        patron: patronInternal,
        backingMode: ticketOption?.backingMode,
        parentGroupId: ticketOptionGroup.id,
      });
    }
    onChangeQuantity(selectionsWithoutOption);
  };
  const sortedTicketOptions = sortBy(ticketOptions, 'ordering');
  const numSelectedInGroup = getSelectedOptionsCount(
    [...(optionOrders || []), ...(partyOptionOrders || [])],
    ticketOptions as core.TicketOption[]
  );
  return {
    handleChangeQuantity,
    sortedTicketOptions,
    numSelectedInGroup,
  };
};

export const generateOptionGroupOptionProps = ({
  ticketOption,
  ticketOptionGroup,
  ticketOptions,
  partySize,
  numSelectedInGroup,
  ticketOptionPrices,
  optionOrders,
  optionExclusion,
  useInclusivePricing,
}: {
  ticketOption: core.ITicketOption;
  ticketOptionGroup: core.ITicketOptionGroup;
  ticketOptions: core.ITicketOption[];
  partySize: number;
  numSelectedInGroup: number;
  ticketOptionPrices: core.IPrice[];
  optionOrders: core.IOptionOrder[];
  optionExclusion: core.IOptionExclusion[];
  useInclusivePricing: boolean;
}) => {
  const ticketOptionId = ticketOption.id as number;
  const numSelectedOfSupplement = size(findOrdersMatchingOptions(optionOrders, [ticketOption]));
  const numFiredToKitchen: number = optionOrders.filter(
    (option) => ticketOptionId === option.optionId && option.sentToKitchen
  ).length;
  const maxQuantity = getMaxQuantityForSupplementInGroup(
    ticketOptionGroup,
    partySize,
    numSelectedOfSupplement,
    numSelectedInGroup,
    ticketOption.currentQuantity,
    numFiredToKitchen
  );
  const minRequiredIncrement = getMinRequiredIncrement(
    ticketOptionGroup,
    partySize,
    ticketOptions,
    optionExclusion
  );
  const relevantTicketOptionPrice = find(ticketOptionPrices, {
    id: ticketOption.id,
  });
  const price =
    (useInclusivePricing
      ? relevantTicketOptionPrice?.amountIncludingFees
      : relevantTicketOptionPrice?.amount) ?? 0;

  const isExcluded = getIsExcluded(
    ticketOptionId,
    ticketOptionGroup,
    ticketOptions,
    optionOrders,
    optionExclusion
  );
  return {
    isExcluded,
    isSoldOut: ticketOption.currentQuantity === 0,
    numSelectedOfSupplement,
    maxQuantity,
    minRequiredIncrement,
    price,
    numFiredToKitchen,
  };
};

export const generateModifierDialogProps = ({
  ticketType,
  ticketOption,
  ticketOptionGroup,
  allTicketOptions,
  optionOrders,
  otherOptionOrders,
  ticketOptionPrices,
  partySize,
  onChangeQuantity,
  patron,
  adminOverride,
  useInclusivePricing,
}: {
  ticketType: core.ITicketType;
  ticketOption: core.ITicketOption;
  ticketOptionGroup: core.ITicketOptionGroup;
  allTicketOptions: core.ITicketOption[];
  optionOrders: core.IOptionOrder[];
  otherOptionOrders: core.IOptionOrder[];
  ticketOptionPrices: core.IPrice[];
  partySize: number;
  onChangeQuantity(selectedOptions: core.IOptionOrder[]): void;
  patron?: core.IPatron | null;
  adminOverride?: boolean;
  useInclusivePricing: boolean;
}) => {
  const optionsSentToKitchen = filter(
    optionOrders,
    (option) => !!option.sentToKitchen && option.optionId === ticketOption.id
  );

  const handleChangeQuantity = (newQuantity: number) => {
    const firstOrder = first(optionOrders) ?? {
      optionId: ticketOption.id,
      numPurchased: 1,
      patron,
      backingMode: ticketOption.backingMode,
      parentGroupId: ticketOptionGroup.id,
    };
    if (!adminOverride && newQuantity < optionsSentToKitchen.length) {
      return;
    }
    const numSentToKitchen = Math.min(newQuantity, optionsSentToKitchen.length);
    const orders: core.IOptionOrder[] = [];
    for (let i = 0; i < newQuantity; ++i) {
      const clonedOrder = cloneDeep(firstOrder);
      if (i >= numSentToKitchen) {
        clonedOrder.sentToKitchen = false;
        forEach(clonedOrder.modifierOption, (modifier) => {
          modifier.sentToKitchen = false;
        });
      }
      orders.push(clonedOrder);
    }
    onChangeQuantity(orders);
  };
  const parentTicketOptionId = ticketOption.id as number;
  const mergedOptionOrders = concat(otherOptionOrders, optionOrders);
  const { optionExclusion } = ticketType;

  const allOptionsInGroup = filter(
    allTicketOptions,
    (option) => option.ticketOptionGroupId === ticketOptionGroup.id
  );
  const otherOrdersOfSameOption = size(
    findOrdersMatchingOptions(otherOptionOrders, [ticketOption])
  );
  const otherOrdersOfSameOptionSentToKitchen = filter(
    findOrdersMatchingOptions(otherOptionOrders, [ticketOption]),
    (option) => option.sentToKitchen
  );
  const maxQuantityForOrdersInGroup = getMaxQuantityForSupplementInGroup(
    ticketOptionGroup,
    partySize,
    size(findOrdersMatchingOptions(mergedOptionOrders, [ticketOption])),
    getSelectedOptionsCount(mergedOptionOrders, allOptionsInGroup),
    ticketOption.currentQuantity,
    otherOrdersOfSameOptionSentToKitchen.length
  );
  const maxQuantity = maxQuantityForOrdersInGroup - otherOrdersOfSameOption;
  const minRequiredIncrement = getMinRequiredIncrement(
    ticketOptionGroup,
    partySize,
    allOptionsInGroup,
    ticketType.optionExclusion ?? []
  );
  const disabled = getIsExcluded(
    parentTicketOptionId,
    ticketOptionGroup,
    allTicketOptions,
    mergedOptionOrders,
    optionExclusion
  );
  const hasAllOptionExclusions = hasAllOptionExclusion(
    ticketOptionGroup,
    allOptionsInGroup,
    optionExclusion
  );

  const getAmountForFeature = (option: core.IPrice | undefined, useInclusivePricing: boolean) => {
    return useInclusivePricing ? option?.amountIncludingFees : option?.amount;
  };

  let price = 0;
  forEach(optionOrders, (optionOrder) => {
    price +=
      getAmountForFeature(
        find(ticketOptionPrices, (price) => price.id === optionOrder.optionId),
        useInclusivePricing
      ) ?? 0;
    forEach(optionOrder.modifierOption, (modifier) => {
      price +=
        getAmountForFeature(
          find(ticketOptionPrices, (price) => price.id === modifier.optionId),
          useInclusivePricing
        ) ?? 0;
    });
  });

  const quantity = optionOrders.length;
  const disableDecrement = disabled || quantity <= 1;
  const disableIncrement = disabled || quantity >= maxQuantity;

  const handleDecrementQuantity = () => {
    let newQuantity = quantity - 1;
    if (hasAllOptionExclusions && newQuantity < minRequiredIncrement) {
      newQuantity = 0;
    }
    if (newQuantity >= 0 || disableDecrement) {
      handleChangeQuantity(newQuantity);
    }
  };
  const handleIncrementQuantity = () => {
    let newQuantity = quantity + 1;
    if (hasAllOptionExclusions && newQuantity < minRequiredIncrement) {
      newQuantity = minRequiredIncrement;
    }
    if (newQuantity <= maxQuantity || disableIncrement) {
      handleChangeQuantity(newQuantity);
    }
  };
  return {
    quantity,
    disableDecrement,
    disableIncrement,
    handleDecrementQuantity,
    handleIncrementQuantity,
    price,
  };
};

export function sortOptionOrder(
  optionInfoMap: Dictionary<{
    option: core.ITicketOption;
    optionGroup: core.ITicketOptionGroup;
  }>
) {
  return [
    (order: core.OptionOrder) => optionInfoMap[order.optionId]?.optionGroup?.ordering,
    (order: core.OptionOrder) => optionInfoMap[order.optionId]?.optionGroup?.id,
    (order: core.OptionOrder) => optionInfoMap[order.optionId]?.option?.ordering,
    (order: core.OptionOrder) => optionInfoMap[order.optionId]?.option?.id,
  ];
}

export function getTicketOptionInfoMap({
  ticketOption,
  ticketOptionGroup,
}: {
  ticketOption: core.ITicketOption[];
  ticketOptionGroup: core.ITicketOptionGroup[];
}) {
  const optionInfoById: Dictionary<{
    option: core.ITicketOption;
    optionGroup: core.ITicketOptionGroup;
  }> = {};

  forEach(ticketOption, (option) => {
    optionInfoById[option.id ?? ''] = {
      option,
      optionGroup: find(
        ticketOptionGroup,
        (optionGroup) => optionGroup.id === option.ticketOptionGroupId
      ) as core.ITicketOptionGroup,
    };
  });
  return optionInfoById;
}

export function generateOptionDiff(
  newOptions: core.IOptionOrder[],
  originalOptions: core.IOptionOrder[]
) {
  const originalOptionsMap = toOptionMap(originalOptions);
  const addOptions: core.IOptionOrder[] = [];
  newOptions.forEach((option) => {
    const optionKey = optionToKey(option);
    if (!originalOptionsMap[optionKey] || originalOptionsMap[optionKey].length === 0) {
      addOptions.push(option);
    } else {
      originalOptionsMap[optionKey].pop();
    }
  });

  const removeOptions: core.IOptionOrder[] = concat([], ...Object.values(originalOptionsMap));

  return { addOptions, removeOptions };
}

function toOptionMap(options: core.IOptionOrder[]) {
  const optionMap: Dictionary<core.IOptionOrder[]> = {};

  options.forEach((option) => {
    const optionKey = optionToKey(option);
    if (!optionMap[optionKey]) {
      optionMap[optionKey] = [option];
    } else {
      optionMap[optionKey].push(option);
    }
  });

  return optionMap;
}

function optionToKey(option: core.IOptionOrder) {
  const optionModifierIds = (option.modifierOption || [])
    .map((optionModifier) => optionModifier.optionId)
    .filter((id) => !!id)
    .sort()
    .join('-');
  return `${option.optionId}-${optionModifierIds}-${option.patron?.id}`;
}
